// src/MoneyPlanner.js
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = 'https://snap2excel-backend.onrender.com';

function MoneyPlanner({ token, refreshKey }) {
  const [loading, setLoading] = useState(true);

  // high-level plan (from /api/money/plan)
  const [plan, setPlan] = useState(null);

  // detailed settings (from /api/money/settings)
  const [settings, setSettings] = useState(null);

  const [monthlySpend, setMonthlySpend] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  // form used in the Edit panel
  const [form, setForm] = useState({
    income_amount: '',
    income_frequency: 'monthly',
    bills: [], // [{ name, amount, frequency }]
  });

  useEffect(() => {
    if (!token) return;

    const auth = axios.create({
      baseURL: API,
      headers: { Authorization: `Bearer ${token}` },
    });

    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, '0')}`;

    const parseToMonthKey = (dateStr) => {
      if (!dateStr) return null;
      const m = String(dateStr).match(
        /(\d{1,4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,4})/
      );
      if (!m) return null;

      let a = parseInt(m[1], 10);
      let b = parseInt(m[2], 10);
      let c = parseInt(m[3], 10);

      let year, month;
      if (a > 1900) {
        year = a;
        month = b;
      } else if (c > 1900) {
        year = c;
        month = b;
      } else {
        year = c < 100 ? 2000 + c : c;
        month = b;
      }

      if (!month || month < 1 || month > 12) return null;
      return `${year}-${String(month).padStart(2, '0')}`;
    };

    const load = async () => {
      setLoading(true);
      try {
        const [planRes, settingsRes, receiptsRes] = await Promise.all([
          auth.get('/api/money/plan'),
          auth.get('/api/money/settings'),
          auth.get('/api/receipts'),
        ]);

        const p = planRes.data || {};
        const s = settingsRes.data || {};

        setPlan(p);
        setSettings(s);

        // pre-fill edit form with detailed bills
        const billsFromApi = Array.isArray(s.bills) ? s.bills : [];
        setForm({
          income_amount: s.income_amount ?? '',
          income_frequency: s.income_frequency || 'monthly',
          bills:
            billsFromApi.length > 0
              ? billsFromApi
              : [
                  {
                    name: '',
                    amount: '',
                    frequency: 'monthly',
                  },
                ],
        });

        // month spend based on receipts
        const receipts = receiptsRes.data || [];
        let total = 0;

        receipts.forEach((r) => {
          let mk = parseToMonthKey(r.date);
          if (!mk) {
            mk = currentMonthKey;
          }
          if (mk !== currentMonthKey) return;
          const amt = Number(r.total) || 0;
          total += amt;
        });

        setMonthlySpend(total);
      } catch (err) {
        console.error('Money planner load error', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token, refreshKey]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBillChange = (index, field, value) => {
    setForm((prev) => {
      const bills = [...prev.bills];
      bills[index] = { ...bills[index], [field]: value };
      return { ...prev, bills };
    });
  };

  const addBillRow = () => {
    setForm((prev) => ({
      ...prev,
      bills: [
        ...prev.bills,
        { name: '', amount: '', frequency: prev.income_frequency || 'monthly' },
      ],
    }));
  };

  const removeBillRow = (index) => {
    setForm((prev) => {
      const bills = [...prev.bills];
      if (bills.length === 1) return prev; // always keep at least one row
      bills.splice(index, 1);
      return { ...prev, bills };
    });
  };

  const savePlan = async () => {
    if (!token) return;

    const auth = axios.create({
      baseURL: API,
      headers: { Authorization: `Bearer ${token}` },
    });

    const payload = {
      income_amount: Number(form.income_amount) || 0,
      income_frequency:
        form.income_frequency === 'weekly' ||
        form.income_frequency === 'monthly'
          ? form.income_frequency
          : 'monthly',
      bills: form.bills.map((b) => ({
        name: b.name || 'Bill',
        amount: Number(b.amount) || 0,
        frequency:
          b.frequency === 'weekly' ||
          b.frequency === 'monthly' ||
          b.frequency === 'yearly'
            ? b.frequency
            : 'monthly',
      })),
    };

    try {
      await auth.post('/api/money/settings', payload);
      setIsEditing(false);

      // reload summary plan after saving
      const res = await auth.get('/api/money/plan');
      setPlan(res.data || {});
    } catch (err) {
      console.error('Save plan error', err);
      alert('Could not save income & bills');
    }
  };

  if (loading) {
    return (
      <div className="card-screen money-planner-root">
        <section className="planner-header-card">
          <h2 className="section-title">Money planner</h2>
          <p className="section-subtitle">Loading your plan…</p>
        </section>
      </div>
    );
  }

  // If user has never set anything yet
  if (!plan || !plan.hasData) {
    return (
      <div className="card-screen money-planner-root">
        <section className="planner-header-card">
          <div className="planner-header-main">
            <h2 className="section-title">Money planner</h2>
            <p className="section-subtitle">
              Simple view of what comes in, what your bills take, and what&apos;s
              really left for you. No bank connection – everything stays inside
              Snap2Excel.
            </p>
          </div>
          <div className="planner-header-meta">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setIsEditing(true)}
            >
              Edit income &amp; bills
            </button>
          </div>
        </section>

        <section className="planner-main-card">
          <p className="planner-empty-text">
            You haven&apos;t added your income and bills yet. Once you do, we&apos;ll
            show how much is truly left each month and how much to save weekly.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setIsEditing(true)}
          >
            Start money plan
          </button>
        </section>

        {isEditing && (
          <PlannerEditPanel
            form={form}
            onFieldChange={handleFieldChange}
            onBillChange={handleBillChange}
            onAddBill={addBillRow}
            onRemoveBill={removeBillRow}
            onCancel={() => setIsEditing(false)}
            onSave={savePlan}
          />
        )}
      </div>
    );
  }

  // numbers from /api/money/plan
  const monthlyIncome = Number(plan.monthlyIncome || 0);
  const monthlyBills = Number(plan.monthlyBills || 0);
  const leftoverBeforeSpend = Number(plan.leftover || 0);
  const spentThisMonth = Number(monthlySpend || 0);
  const leftoverAfterSpend = Math.max(0, leftoverBeforeSpend - spentThisMonth);
  const weeklyTarget = Number(plan.weeklyInvest || 0);
  const savingsBudget = Number(plan.savingsBudget || 0);
  const yearlyIfFollow = savingsBudget * 12;

  return (
    <div className="card-screen money-planner-root">
      {/* HEADER CARD */}
      <section className="planner-header-card">
        <div className="planner-header-main">
          <h2 className="section-title">Money planner</h2>
          <p className="section-subtitle">
            Simple view of what comes in, what your bills take, and what&apos;s
            really left for you. No bank connection – everything stays inside
            Snap2Excel.
          </p>
        </div>
        <div className="planner-header-meta">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsEditing(true)}
          >
            Edit income &amp; bills
          </button>
          <span className="planner-pill planner-pill-green">
            This month: £{spentThisMonth.toFixed(2)} spent
          </span>
          <span className="planner-pill planner-pill-grey">
            Weekly saving target: £{weeklyTarget.toFixed(2)}
          </span>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="planner-main-card">
        <div className="planner-main-top">
          <h3 className="planner-main-title">This is where you stand</h3>
          <p className="planner-main-copy">
            You bring in about <strong>£{monthlyIncome.toFixed(2)}</strong> per
            month. Bills use roughly{' '}
            <strong>£{monthlyBills.toFixed(2)}</strong>, leaving{' '}
            <strong>£{leftoverBeforeSpend.toFixed(2)}</strong> for day-to-day
            life. We suggest putting{' '}
            <strong>£{weeklyTarget.toFixed(2)}</strong> aside each week for
            savings or future plans.
          </p>
        </div>

        <div className="planner-summary-layout">
          {/* LEFT: numbers table + progress */}
          <div className="planner-summary-left">
            <div className="planner-summary-table">
              <div className="planner-summary-row">
                <span>Income (per month)</span>
                <span>£{monthlyIncome.toFixed(2)}</span>
              </div>
              <div className="planner-summary-row">
                <span>Bills (per month)</span>
                <span>£{monthlyBills.toFixed(2)}</span>
              </div>
              <div className="planner-summary-row">
                <span>Left after bills</span>
                <span>£{leftoverBeforeSpend.toFixed(2)}</span>
              </div>
              <div className="planner-summary-row">
                <span>Spent this month (tracked)</span>
                <span>£{spentThisMonth.toFixed(2)}</span>
              </div>
              <div className="planner-summary-row">
                <span>Leftover after spending</span>
                <span>£{leftoverAfterSpend.toFixed(2)}</span>
              </div>
            </div>

            <div className="planner-progress-text">
              This month you spent{' '}
              <strong>£{spentThisMonth.toFixed(2)}</strong> from the money left
              after bills. That leaves{' '}
              <strong>£{leftoverAfterSpend.toFixed(2)}</strong> still free for
              you this month.
            </div>
          </div>

          {/* RIGHT: green suggested card */}
          <div className="planner-summary-right">
            <div className="planner-suggested-card">
              <div className="planner-suggested-label">
                Suggested weekly saving
              </div>
              <div className="planner-suggested-amount">
                £{weeklyTarget.toFixed(2)}
              </div>
              <div className="planner-suggested-tag">
                Simple rule: savings first, then enjoy the rest without guilt.
              </div>
              <p className="planner-suggested-note">
                Hit that weekly target and you&apos;re on track for about{' '}
                <strong>£{yearlyIfFollow.toFixed(0)}</strong> saved in a year.
              </p>
            </div>
          </div>
        </div>

        <div className="planner-footer-text">
          <h4>Where your money is going</h4>
          <p>
            Snap2Excel keeps a clean record of every receipt you scan, so you
            can see exactly where that leftover money goes each month – without
            guessing. Check the Dashboard for categories like food, fuel or
            shopping and spot any leaks fast.
          </p>
        </div>
      </section>

      {isEditing && (
        <PlannerEditPanel
          form={form}
          onFieldChange={handleFieldChange}
          onBillChange={handleBillChange}
          onAddBill={addBillRow}
          onRemoveBill={removeBillRow}
          onCancel={() => setIsEditing(false)}
          onSave={savePlan}
        />
      )}
    </div>
  );
}

function PlannerEditPanel({
  form,
  onFieldChange,
  onBillChange,
  onAddBill,
  onRemoveBill,
  onCancel,
  onSave,
}) {
  return (
    <div className="planner-edit-backdrop">
      <div className="planner-edit-panel">
        <h3>Edit income &amp; bills</h3>
        <p className="planner-edit-sub">
          We only need rough monthly amounts. You can adjust any time.
        </p>

        <label className="planner-edit-label">
          Income per month (£)
          <input
            className="auth-input"
            type="number"
            step="0.01"
            value={form.income_amount}
            onChange={(e) => onFieldChange('income_amount', e.target.value)}
          />
        </label>

        <label className="planner-edit-label">
          Income frequency
          <select
            className="auth-input"
            value={form.income_frequency}
            onChange={(e) => onFieldChange('income_frequency', e.target.value)}
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
          </select>
        </label>

        <hr className="planner-edit-divider" />

        <div className="planner-bills-header-row">
          <span>Bill name</span>
          <span>Amount (£)</span>
          <span>How often</span>
          <span />
        </div>

        {form.bills.map((b, idx) => (
          <div key={idx} className="planner-bills-row">
            <input
              className="auth-input"
              type="text"
              placeholder="Rent, Council Tax, Phone…"
              value={b.name}
              onChange={(e) => onBillChange(idx, 'name', e.target.value)}
            />
            <input
              className="auth-input"
              type="number"
              step="0.01"
              value={b.amount}
              onChange={(e) => onBillChange(idx, 'amount', e.target.value)}
            />
            <select
              className="auth-input"
              value={b.frequency}
              onChange={(e) => onBillChange(idx, 'frequency', e.target.value)}
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="yearly">Yearly</option>
            </select>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => onRemoveBill(idx)}
            >
              Remove
            </button>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-outline"
          style={{ marginTop: 8 }}
          onClick={onAddBill}
        >
          + Add another bill
        </button>

        <div className="planner-edit-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={onSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default MoneyPlanner;
